import React, { useState } from 'react';
import {
  Alert,
  AlertColor,
  Button,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  TextField,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import CentsLoader from 'components/common/centsLoader/CentsLoader';
import { buildDefaultCustomerProfileOptions } from 'utils/twilioCustomerProfile';
import { TwilioBrandRegistrationType } from 'constants/twilio/twilioBrand';
import { CampaignProfile } from 'types/twilio/campaignProfile';
import { CreateTwilioBrandOptions } from 'types/twilio/twilioBrand';
import { CampaignCreationFormOptions } from 'types/twilio/twilioCampaign';
import { CampaignDescriptionField } from '../components/CampaignDescriptionField';
import { useCampaignProfile } from '../hooks/useCampaignProfile';
import { useGetTwilioBrand } from '../queries/useGetTwilioBrand';
import { useGetTwilioCampaign } from '../queries/useGetTwilioCampaign';
import styles from './TwilioBrandAndCampaign.module.scss';

interface TwilioBrandAndCampaignProps {
  brandId: string | null | undefined;
  campaignId: string | null | undefined;
  messagingServiceId: string | null | undefined;
  businessId: number;
  businessName: string;
  businessWebsiteUrl: string;
  savedCampaignProfile: CampaignProfile | null | undefined;
  handleBrandCreationEvent: (opts: CreateTwilioBrandOptions) => void;
  handleCampaignCreationEvent?: (opts: CampaignCreationFormOptions) => void;
}

export const TwilioBrandAndCampaign = ({
  brandId,
  campaignId,
  messagingServiceId,
  businessId,
  businessName,
  businessWebsiteUrl,
  savedCampaignProfile,
  handleBrandCreationEvent,
  handleCampaignCreationEvent,
}: TwilioBrandAndCampaignProps) => {
  const { data: twilioBrandResponse, isLoading: isTwilioBrandLoading } = useGetTwilioBrand(
    businessId,
    brandId,
  );
  const { data: twilioCampaignResponse, isLoading: isTwilioCampaignLoading } = useGetTwilioCampaign(
    {
      businessId,
      campaignId,
      messagingServiceId,
    },
  );
  const [createBrandOptions, setCreateBrandOptions] = useState<CreateTwilioBrandOptions>({
    brandType: TwilioBrandRegistrationType.LOW_VOLUME,
  });
  const [campaignOptions, setCampaignOptions] = useState<CampaignCreationFormOptions>({
    businessName,
  });

  const customerProfileOptions = buildDefaultCustomerProfileOptions({
    savedCampaignProfile,
    businessName,
    businessWebsiteUrl,
  });

  const {
    campaignDescription,
    onCampaignDescriptionChange,
    trimmedCampaignDescriptionLength,
    isCampaignDescriptionValid,
    isSavingCampaignProfile,
    handleSaveCampaignProfile,
    resetCampaignDescription,
  } = useCampaignProfile({ businessId, savedCampaignProfile, customerProfileOptions });

  const handleBrandInputChange =
    (field: keyof CreateTwilioBrandOptions) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setCreateBrandOptions((prev) => ({
        ...prev,
        [field]: event.target.value as TwilioBrandRegistrationType,
      }));
    };

  const handleCampaignInputChange =
    (field: keyof CampaignCreationFormOptions) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setCampaignOptions((prev) => ({
        ...prev,
        [field]: event.target.value,
      }));
    };

  const handleCreateCampaign = async () => {
    try {
      await handleSaveCampaignProfile();
    } catch (error) {
      // Error is already handled in the mutation's onError callback, so we just return early here.
      return;
    }

    handleCampaignCreationEvent?.({ businessName: campaignOptions.businessName.trim() });
  };

  const isCampaignReadyToCreate =
    twilioBrandResponse?.brandDetails?.status === 'APPROVED' &&
    !!campaignOptions.businessName.trim();

  const brandStatusColor: AlertColor =
    twilioBrandResponse?.brandDetails?.status === 'APPROVED'
      ? 'success'
      : twilioBrandResponse?.brandDetails?.status === 'FAILED'
      ? 'error'
      : 'info';

  return (
    <div className={styles.wrapper}>
      {((!!brandId && isTwilioBrandLoading) || (campaignId && isTwilioCampaignLoading)) && (
        <CentsLoader />
      )}

      {!brandId && (
        <div className="d-flex flex-column align-items-start">
          <FormControl>
            <p id="brand-radio-buttons-brand-type">Brand Type</p>

            <RadioGroup
              aria-labelledby="brand-radio-buttons-brand-type"
              value={createBrandOptions.brandType}
              onChange={handleBrandInputChange('brandType')}
              name="brand-radio-buttons-group"
            >
              <FormControlLabel
                value={TwilioBrandRegistrationType.LOW_VOLUME}
                control={<Radio />}
                label="Low-Volume Standard Brand"
              />
              <FormControlLabel
                value={TwilioBrandRegistrationType.STANDARD}
                control={<Radio />}
                label="Standard Brand"
              />
            </RadioGroup>
          </FormControl>

          <Button
            style={{ marginLeft: 0 }}
            variant="outlined"
            onClick={() => handleBrandCreationEvent(createBrandOptions)}
          >
            Create brand
          </Button>
        </div>
      )}

      <div style={{ gap: '10px' }} className="d-flex flex-wrap">
        {!!twilioBrandResponse?.brandDetails && (
          <Grid container spacing={2} columns={2} className="w-100">
            <Grid size="auto" display="flex" alignItems="center">
              Brand Status
            </Grid>

            <Grid size="auto">
              <Alert severity={brandStatusColor} icon={false}>
                {twilioBrandResponse.brandDetails?.status}
              </Alert>
            </Grid>
          </Grid>
        )}

        {!!brandId && !campaignId && (
          <div className="d-flex flex-column align-items-start w-100" style={{ gap: '10px' }}>
            <TextField
              type="text"
              variant="outlined"
              color="secondary"
              label="Business name"
              onChange={handleCampaignInputChange('businessName')}
              value={campaignOptions.businessName}
              fullWidth
            />

            {isCampaignReadyToCreate && (
              <CampaignDescriptionField
                value={campaignDescription}
                onChange={onCampaignDescriptionChange}
                trimmedLength={trimmedCampaignDescriptionLength}
                isValid={isCampaignDescriptionValid}
                onReset={resetCampaignDescription}
                showGuidance={false}
              />
            )}

            <div className="d-flex" style={{ gap: '10px', paddingLeft: '10px' }}>
              {isCampaignReadyToCreate && (
                <Button
                  variant="outlined"
                  style={{ margin: 0 }}
                  disabled={isSavingCampaignProfile || !isCampaignDescriptionValid}
                  onClick={handleSaveCampaignProfile}
                >
                  Save
                </Button>
              )}

              <Button
                style={{ margin: 0 }}
                variant="outlined"
                onClick={handleCreateCampaign}
                disabled={
                  isSavingCampaignProfile || !isCampaignReadyToCreate || !isCampaignDescriptionValid
                }
              >
                Create campaign
              </Button>
            </div>
          </div>
        )}

        {!!twilioCampaignResponse?.campaign && (
          <Grid container spacing={2} className="w-100">
            <Grid size="auto" display="flex" alignItems="center">
              Campaign Status
            </Grid>

            <Grid size="auto">
              <Alert severity={brandStatusColor} icon={false}>
                {twilioCampaignResponse?.campaign?.campaignStatus}
              </Alert>
            </Grid>
          </Grid>
        )}
      </div>
    </div>
  );
};
